// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
import type { AppContext } from '../types';
import { squarify } from '../treemap';
import { fmtDollars, fmtMoney, fmtPercent, escapeHtml } from '../format';

const CAT_COLOURS = [
  '#1c3a5e', '#127a4f', '#a8842c', '#a4243b', '#4a6d8c',
  '#6b8e4e', '#8a5b2c', '#7c4bd0', '#2b8b8b', '#b26a00',
];

function catColourMap(names: string[]): Map<string, string> {
  const m = new Map<string, string>();
  names.forEach((n, i) => m.set(n, CAT_COLOURS[i % CAT_COLOURS.length]));
  return m;
}

export function renderCategories(root: HTMLElement, ctx: AppContext): void {
  const { data } = ctx;
  const cats = data.summary.categories;
  const colours = catColourMap(cats.map((c) => c.name));
  const total = cats.reduce((s, c) => s + c.total, 0);

  root.innerHTML = `
    <div class="view-head">
      <h2>Where the money goes</h2>
      <p>The ${cats.length} high-level expense categories, sized by total spend across every parliamentarian. Click a block to rank parliamentarians by that category. Two categories — Employee Travel and Office Facilities — are reported as quarterly aggregate totals rather than itemised.</p>
    </div>
    <div class="chart-card treemap-wrap" style="margin-bottom:var(--space-lg)">
      <h3>Expense composition</h3>
      <div class="subtitle">Total ${fmtDollars(total)} since ${escapeHtml(data.summary.meta.windowStartLabel)}. Block area = share of all spending.</div>
      <div id="tm"></div>
    </div>
    <div class="chart-card">
      <h3>Party spending profiles</h3>
      <div class="subtitle">Average spend <em>per member</em> in each category, so parties of different sizes compare fairly. Darker = higher within that column. Hover any cell for exact figures.</div>
      <div class="matrix-wrap" id="matrix"></div>
    </div>`;

  drawTreemap(document.getElementById('tm')!, cats, colours, total, ctx);
  drawMatrix(document.getElementById('matrix')!, data, colours);
}

function drawTreemap(
  el: HTMLElement,
  cats: { name: string; total: number }[],
  colours: Map<string, string>,
  total: number,
  ctx: AppContext
): void {
  const W = 1000;
  const H = 520;
  const rects = squarify(cats.map((c) => c.total), W, H);
  const blocks = rects
    .map((r, i) => {
      const c = cats[i];
      const col = colours.get(c.name)!;
      const pct = total ? c.total / total : 0;
      const showLabel = r.w > 74 && r.h > 34;
      const label = showLabel
        ? `<text x="${r.x + 10}" y="${r.y + 22}" font-size="15">${escapeHtml(c.name)}</text>
           <text x="${r.x + 10}" y="${r.y + 42}" font-size="13" opacity="0.9" font-family="var(--font-mono)">${fmtMoney(c.total)} · ${fmtPercent(pct)}</text>`
        : '';
      const tip = `${escapeHtml(c.name)}: ${fmtDollars(c.total)} (${fmtPercent(pct, 1)})`;
      return `<g class="tm-cell" data-cat="${escapeHtml(c.name)}" data-tip="${tip}" role="button" tabindex="0" aria-label="${tip}" style="cursor:pointer">
        <rect x="${r.x}" y="${r.y}" width="${Math.max(0, r.w)}" height="${Math.max(0, r.h)}" fill="${col}"></rect>
        ${label}
      </g>`;
    })
    .join('');
  el.innerHTML = `<svg class="treemap" viewBox="0 0 ${W} ${H}" preserveAspectRatio="xMidYMid meet" role="img" aria-label="Treemap of expense categories">${blocks}</svg>`;

  const rankByCat = (cat: string) => {
    try {
      const raw = JSON.parse(localStorage.getItem('ampx.rankings') || '{}');
      localStorage.setItem('ampx.rankings', JSON.stringify({ ...raw, metric: cat }));
    } catch { /* ignore */ }
    ctx.goto('rankings');
  };
  el.querySelectorAll<SVGElement>('.tm-cell').forEach((cell) => {
    cell.addEventListener('click', () => rankByCat(cell.dataset.cat!));
    cell.addEventListener('keydown', (e) => {
      if ((e as KeyboardEvent).key === 'Enter' || (e as KeyboardEvent).key === ' ') { e.preventDefault(); rankByCat(cell.dataset.cat!); }
    });
  });
}

function drawMatrix(el: HTMLElement, data: AppContext['data'], colours: Map<string, string>): void {
  const cats = data.summary.categories.map((c) => c.name);
  // parties with a meaningful member count, biggest first
  const parties = data.summary.parties.filter((p) => p.members >= 1 && p.code !== 'FMR');

  // per-member average per (party, cat)
  const avg = (pcode: string, cat: string): number => {
    const p = parties.find((x) => x.code === pcode)!;
    return (p.byCategory[cat] || 0) / Math.max(1, p.members);
  };
  // column max for normalisation
  const colMax = new Map<string, number>();
  for (const cat of cats) colMax.set(cat, Math.max(...parties.map((p) => avg(p.code, cat)), 1));

  const head = `<tr><th class="row"></th>${cats
    .map((c) => `<th class="col" data-tip="${escapeHtml(c)}"><div style="max-width:70px;white-space:normal;line-height:1.1">${escapeHtml(c)}</div></th>`)
    .join('')}</tr>`;
  const rows = parties
    .map((p) => {
      const cells = cats
        .map((cat) => {
          const v = avg(p.code, cat);
          const intensity = v / (colMax.get(cat) || 1);
          const col = colours.get(cat)!;
          const bg = mix('#ffffff', col, 0.12 + intensity * 0.78);
          const txt = intensity > 0.55 ? '#fff' : 'var(--text-primary)';
          const tip = `${escapeHtml(p.name)} — ${escapeHtml(cat)}\nPer member: ${fmtDollars(v)}\nParty total: ${fmtDollars(p.byCategory[cat] || 0)}`;
          return `<td style="background:${bg};color:${txt}" data-tip="${tip}">${v > 0 ? fmtMoney(v) : '·'}</td>`;
        })
        .join('');
      return `<tr><th class="row" data-tip="${escapeHtml(p.name)}: ${p.members} members">${escapeHtml(p.name)}</th>${cells}</tr>`;
    })
    .join('');
  el.innerHTML = `<table class="matrix">${head}${rows}</table>`;
}

/** Blend hex colour a toward b by t (0..1). */
function mix(a: string, b: string, t: number): string {
  const pa = hex(a);
  const pb = hex(b);
  const r = Math.round(pa[0] + (pb[0] - pa[0]) * t);
  const g = Math.round(pa[1] + (pb[1] - pa[1]) * t);
  const bl = Math.round(pa[2] + (pb[2] - pa[2]) * t);
  return `rgb(${r},${g},${bl})`;
}
function hex(h: string): [number, number, number] {
  const m = /^#?([0-9a-f]{6})$/i.exec(h);
  if (!m) return [255, 255, 255];
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
